import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import { PageBar, Content } from '../components/Layout'
import { Badge, ErrorBox, KV, Loading, Panel } from '../components/ui'
import { api } from '../lib/api'
import { gbp, longDate, shortDate } from '../lib/format'
import type { AppSettings, Client, Invoice } from '../types'

const STATUSES = ['draft', 'sent', 'paid', 'overdue'] as const

export function InvoiceDetail() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['invoice', id], queryFn: () => api.get<Invoice>(`/invoices/${id}`),
  })
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.get<Client[]>('/clients') })
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.get<AppSettings>('/settings') })

  const setStatus = useMutation({
    mutationFn: (status: string) => api.post(`/invoices/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['overview'] })
    },
  })

  if (isLoading) return <><PageBar title="Invoice" /><Content><Loading /></Content></>
  if (error || !data) return <><PageBar title="Invoice" /><Content><ErrorBox error={error} /></Content></>

  const client = clients.data?.find((c) => c.id === data.client_id)

  return (
    <>
      <PageBar title={data.number} actions={<Link className="btn ghost" to="/invoices">All invoices</Link>} />
      <Content>
        <div className="cols">
          <section>
            <Panel className="invoice">
              <div className="inv-head">
                <div>
                  <div className="inv-brand">{settings.data?.studio_name ?? 'Old Street Studios'}</div>
                  <div className="muted">
                    {settings.data?.studio_address}<br />{settings.data?.studio_email}
                  </div>
                </div>
                <div className="inv-num">
                  <div className="inv-brand">{data.number}</div>
                  <div className="muted">
                    Issued {longDate(data.issued_on)}
                    {data.due_on && <><br />Due {longDate(data.due_on)}</>}
                  </div>
                </div>
              </div>

              <div className="inv-to">
                <span className="stat-label">Bill to</span>
                <b>{client?.name ?? '—'}</b>
                <div className="muted">{client?.email ?? ''}</div>
              </div>

              <table className="inv-lines">
                <thead>
                  <tr><th>Description</th><th style={{ width: 120, textAlign: 'right' }}>Amount</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{data.description}</td>
                    <td style={{ textAlign: 'right' }}>{gbp(data.net_pence)}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ textAlign: 'right' }}>Net</td>
                    <td style={{ textAlign: 'right' }}>{gbp(data.net_pence)}</td>
                  </tr>
                  {data.vat_pence > 0 && (
                    <tr>
                      <td style={{ textAlign: 'right' }}>VAT {settings.data?.vat_rate_pct}%</td>
                      <td style={{ textAlign: 'right' }}>{gbp(data.vat_pence)}</td>
                    </tr>
                  )}
                  <tr className="total">
                    <td style={{ textAlign: 'right' }}><b>Total</b></td>
                    <td style={{ textAlign: 'right' }}><b>{gbp(data.gross_pence)}</b></td>
                  </tr>
                </tfoot>
              </table>
            </Panel>
          </section>

          <aside>
            <Panel pad>
              <div className="kv big"><span>Total</span><b>{gbp(data.gross_pence)}</b></div>
              <KV label="Status"><Badge kind="inv" value={data.status} /></KV>
              {data.paid_at && <KV label="Paid">{shortDate(data.paid_at)}</KV>}
              {data.booking_id && (
                <KV label="Booking"><Link to={`/bookings/${data.booking_id}`}>#{data.booking_id}</Link></KV>
              )}
              <div className="statusform">
                <select value={data.status} onChange={(event) => setStatus.mutate(event.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </Panel>
            <p className="hint">Xero export lands in M4. Until then this is the record, and the spreadsheet stops.</p>
          </aside>
        </div>
      </Content>
    </>
  )
}
